import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store'
import { authApi, resolveAvatarUrl } from '../api'
import {
  Avatar, Badge, Beacon, Button, Card, DescriptionList, Divider,
  KpiStrip, Loader, Modal, Progress, Sparkline,
} from '../ui'
import {
  HiTicket, HiCheckCircle, HiClock,
  HiArrowRightOnRectangle, HiCog6Tooth, HiChartBar,
} from 'react-icons/hi2'

// ── PROFIL MODALI ───────────────────────────────────────────
// Butun ko'rinish kit komponentlaridan yig'iladi (DESIGN.md 10):
// Modal fokusni ushlab turadi va Esc ni o'zi qabul qiladi, shuning uchun
// bu yerda alohida klaviatura tinglovchisi yo'q.
export default function ProfileModal({ open = true, onClose }) {
  const { user, logout, isSuperAdmin } = useAuthStore()
  const navigate = useNavigate()
  const [st, setSt] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSt(await authApi.myStats())
    } catch {
      setSt(null)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const a = useMemo(() => {
    const d = st || {}
    const fmt = m => m == null ? '—' : m < 60 ? `${m} daq` : `${Math.floor(m / 60)} soat ${m % 60} daq`
    return {
      total:      d.total ?? 0,
      open:       d.active ?? 0,
      done:       d.resolved ?? 0,
      critical:   d.critical ?? 0,
      todayCount: d.today ?? 0,
      rate:       d.total ? Math.round((d.resolved / d.total) * 100) : 0,
      respTxt:    fmt(d.avg_response_min),
      solveTxt:   fmt(d.avg_resolve_min),
      days:       d.trend || [],
    }
  }, [st])

  if (!user) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Profil"
      footer={
        <>
          <Button variant="secondary" icon={HiCog6Tooth}
                  onClick={() => { onClose?.(); navigate('/profile') }}>
            Sozlamalar
          </Button>
          <Button variant="danger" icon={HiArrowRightOnRectangle} onClick={logout}>
            Chiqish
          </Button>
        </>
      }
    >
      {/* Shaxsiy ma'lumot */}
      <div className="ui-row ui-gap-12">
        <Avatar name={user.full_name} src={resolveAvatarUrl(user.avatar_url)} size={48}
                status={user.is_online ? 'ok' : 'off'} />
        <div className="ui-grow">
          <div className="ui-truncate" style={{ fontSize: 'var(--ui-fs-md)', fontWeight: 600 }}>
            {user.full_name}
          </div>
          <div className="ui-row ui-gap-8" style={{ marginTop: 'var(--ui-sp-4)' }}>
            <Badge tone={user.is_online ? 'ok' : 'neutral'}>
              {user.is_online ? 'Onlayn' : 'Oflayn'}
            </Badge>
            <Badge tone="neutral">{isSuperAdmin() ? 'Super Admin' : 'Operator'}</Badge>
          </div>
        </div>
      </div>

      <Divider style={{ margin: 'var(--ui-sp-16) 0' }} />

      <DescriptionList
        items={[
          { key: 'Login', value: user.username },
          ...(user.email ? [{ key: 'Email', value: user.email }] : []),
          {
            key: 'Ikki bosqichli himoya',
            value: (
              <Badge tone={user.totp_enabled ? 'ok' : 'neutral'}>
                {user.totp_enabled ? 'Yoqilgan' : "O'chirilgan"}
              </Badge>
            ),
          },
        ]}
      />

      {/* Analitika */}
      <Card title="Mening statistikam" icon={HiChartBar} style={{ marginTop: 'var(--ui-sp-16)' }}>
        {loading ? (
          <Loader state="working" block />
        ) : (
          <>
            <KpiStrip
              items={[
                { label: 'Jami murojaat',  value: a.total,    icon: HiTicket },
                { label: 'Ochiq',          value: a.open,     icon: HiClock },
                { label: 'Yakunlangan',    value: a.done,     icon: HiCheckCircle },
                { label: 'Kritik (ochiq)', value: a.critical, icon: Beacon },
              ]}
            />

            <div style={{ marginTop: 'var(--ui-sp-16)' }}>
              <div className="ui-between" style={{ marginBottom: 'var(--ui-sp-4)' }}>
                <span className="ui-muted">Yakunlash darajasi</span>
                <b className="ui-nums">{a.rate}%</b>
              </div>
              <Progress value={a.rate} label="Yakunlash darajasi" />
            </div>

            <div style={{ marginTop: 'var(--ui-sp-12)' }}>
              <DescriptionList
                items={[
                  { key: "O'rtacha javob vaqti",  value: <span className="ui-nums">{a.respTxt}</span> },
                  { key: "O'rtacha yechish vaqti", value: <span className="ui-nums">{a.solveTxt}</span> },
                  { key: 'Bugun tayinlangan', value: <span className="ui-nums">{a.todayCount}</span> },
                ]}
              />
            </div>

            <div style={{ marginTop: 'var(--ui-sp-16)' }}>
              <div className="ui-muted" style={{ marginBottom: 'var(--ui-sp-4)' }}>
                Oxirgi 14 kun faolligi
              </div>
              <Sparkline data={a.days} width={400} height={42}
                         preserveAspectRatio="none" style={{ width: '100%' }} />
            </div>
          </>
        )}
      </Card>
    </Modal>
  )
}
